import * as olCoordinate from 'ol/coordinate'
import * as olProj from 'ol/proj'
import * as utmZone from 'utm-zone'
import proj4 from 'proj4'
import bbox from '@turf/bbox'
import { featureCollection, polygon } from '@turf/helpers'
import { coordEach } from '@turf/meta'
import uuidv4 from 'uuid/v4'
import { getCenter, getHeight, getWidth } from 'ol/extent'
import GeoJSONReader from 'jsts/org/locationtech/jts/io/GeoJSONReader.js'
import Valid from 'jsts/org/locationtech/jts/operation/valid/IsValidOp.js'
import booleanValid from '@turf/boolean-valid'

export const layerNames = {
  ortho: 'results/ortho.tiff',
  dem: 'results/dem.tif',
  dem_color: 'results/dem_color.tif',
}

export const getLngLat = sphericalMercatorCoordinate => {
  if (sphericalMercatorCoordinate.some(coordinate => isNaN(coordinate))) {
    return null
  }
  return olProj.transform(sphericalMercatorCoordinate, 'EPSG:3857', 'EPSG:4326')
}
export const getSphericalMercator = lngLat => {
  if (lngLat.some(coordinate => isNaN(coordinate))) {
    return null
  }
  return olProj.transform(lngLat, 'EPSG:4326', 'EPSG:3857')
}

export const getUtmProjFromLngLat = coordinates => {
  if (!coordinates || coordinates.some(coordinate => isNaN(coordinate))) {
    return [null, null]
  }

  const projDef = utmZone.proj({ coordinates })
  const proj = proj4(projDef)

  return [proj, projDef]
}

export const getMapRotationUtm = (map, projDef) => {
  if (!map || !projDef) {
    return null
  }
  const [mapMinX, mapMinY, , mapMaxY] = map
    .getView()
    .calculateExtent(map.getSize())

  if ([mapMinX, mapMinY, mapMaxY].some(coordinate => isNaN(coordinate))) {
    return 0
  }

  const projToSpherical = proj4(projDef, 'EPSG:3857')
  const [utmMinX, utmMinY] = projToSpherical.inverse([mapMinX, mapMinY])
  const [utmMaxX, utmMaxY] = projToSpherical.inverse([mapMinX, mapMaxY])

  return (Math.atan2(utmMaxY - utmMinY, utmMaxX - utmMinX) / Math.PI) * 180 - 90
}

export const getSquareFromCenterCoordinate = (
  coordinateLngLat,
  bufferInMeters,
  rotation,
  proj,
  projDef,
  outputProjection = 'EPSG:3857'
) => {
  return getRectangleFromCenterCoordinate(
    coordinateLngLat,
    [bufferInMeters, bufferInMeters],
    rotation,
    proj,
    projDef,
    outputProjection
  )
}

export const getRectangleFromCenterCoordinate = (
  coordinateLngLat,
  sides,
  rotation,
  proj,
  projDef,
  outputProjection = 'EPSG:3857'
) => {
  const [bufferXInMeters, bufferYInMeters] = sides
  const centerUtmCoord = proj?.forward(coordinateLngLat)
  const cornerCoord = olCoordinate.add(centerUtmCoord?.slice(), [
    -bufferXInMeters / 2,
    -bufferYInMeters / 2,
  ])

  const axisVectors = [
    [1, 0],
    [0, 1],
  ].map(vector => olCoordinate.rotate(vector, (rotation / 180) * Math.PI))

  const projToOutProj = proj4(projDef, outputProjection)

  const sideVectors = axisVectors.map((v, i) => v.map(c => c * sides[i]))
  const rectangle = [
    cornerCoord.slice(),
    olCoordinate.add(cornerCoord.slice(), sideVectors[1]),
    olCoordinate.add(
      olCoordinate.add(cornerCoord.slice(), sideVectors[1]),
      sideVectors[0]
    ),
    olCoordinate.add(cornerCoord.slice(), sideVectors[0]),
    cornerCoord.slice(),
  ].map(c => projToOutProj.forward(c))

  return rectangle
}

export const getExtentFromGeoJson = geojson => {
  const extent = bbox(geojson)
  return olProj.transformExtent(extent, 'EPSG:4326', 'EPSG:3857')
}

export const geometryToFeatureCollection = geometry => {
  const geometryCoordinates = geometry.getCoordinates()
  const featureCoordinates = transformCoords(
    geometry.getType() === 'MultiPolygon'
      ? geometryCoordinates[0]
      : geometryCoordinates,
    'EPSG:3857',
    'EPSG:4326'
  )

  const resultFeatureCollection = featureCollection([
    polygon(featureCoordinates, { gid: uuidv4() }),
  ])

  return resultFeatureCollection
}

export const transformCoords = (coordinates, source, destination) => {
  return isNaN(coordinates[0])
    ? coordinates.map(c => transformCoords(c, source, destination))
    : olProj.transform(coordinates, source, destination)
}

/**
 * Rounds coordinates in a GeoJSON object to limit the number of decimals.
 * Although the GeoJSON spec recommends 6 decimals, we default to 8 decimals
 * since our data needs sub centimeter precision.
 * 8 decimals should give about 0.1 cm precision.
 *
 * This function will alter the GeoJSON object in place to
 * round the coordinates.
 */
export function stripGeoJSONDecimals(geojson, decimals = 8) {
  coordEach(geojson, c => {
    // Rounds stored coordinates, not text shown to users
    /* eslint-disable no-restricted-syntax */
    c[0] = Number(c[0].toFixed(decimals))
    c[1] = Number(c[1].toFixed(decimals))
    /* eslint-enable no-restricted-syntax */
  })

  return geojson
}

/**
 * Given an extent, create the smallest extent that contains the input extent
 * and has the aspect ratio (width/height) requested.
 * @param {import('ol/extent').Extent} extent
 * @param {number} aspectRatio width / height ratio
 * @returns an extent that contains the input extent but has the requested aspect ratio
 */
export function getSmallestContainingExtent(extent, aspectRatio) {
  const width = getWidth(extent)
  const height = getHeight(extent)
  const center = getCenter(extent)

  let newWidth
  let newHeight
  if (width / height < aspectRatio) {
    newWidth = height * aspectRatio
    newHeight = height
  } else {
    newWidth = width
    newHeight = width / aspectRatio
  }

  return [
    center[0] - newWidth / 2,
    center[1] - newHeight / 2,
    center[0] + newWidth / 2,
    center[1] + newHeight / 2,
  ]
}

/**
 * Given an extent, create the largest extent that is contained by the input extent
 * and has the aspect ratio (width/height) requested.
 * @param {import('ol/extent').Extent} extent
 * @param {number} aspectRatio width / height ratio
 * @returns an extent that is contained by the input extent and has the requested aspect ratio
 */
export function getLargestContainedExtent(extent, aspectRatio) {
  const width = getWidth(extent)
  const height = getHeight(extent)
  const center = getCenter(extent)

  let newWidth
  let newHeight
  if (width / height < aspectRatio) {
    newWidth = width
    newHeight = width / aspectRatio
  } else {
    newWidth = height * aspectRatio
    newHeight = height
  }

  return [
    center[0] - newWidth / 2,
    center[1] - newHeight / 2,
    center[0] + newWidth / 2,
    center[1] + newHeight / 2,
  ]
}

export function isValidGeometry(geojsonGeometry) {
  if (geojsonGeometry == null) {
    return false
  }

  if (geojsonGeometry.geometry) {
    geojsonGeometry = geojsonGeometry.geometry
  }

  if (!geojsonGeometry.coordinates) {
    throw new Error(
      'This is not a GeoJSON geometry, or is a MultiGeometry (coordinates are missing).'
    )
  }

  // Run "cheap" valid check in turf
  if (!booleanValid(geojsonGeometry)) {
    return false
  }

  // Run more robust valid check in JSTS
  const reader = new GeoJSONReader()
  const geom = reader.read(geojsonGeometry)
  return new Valid(geom).isValid()
}

export function coordEquals(c1, c2) {
  if (!c1 || !c2 || c1.length !== c2) {
    return false
  }

  return c1[0] === c2[0] && c1[1] === c2[1]
}

/**
 * Tells if an ring is in clockwise direction. Note that the
 * ring must be in GeoJSON format, i.e. last coordinate must
 * be a copy of first coordinate to close the ring.
 *
 * @param {import('ol/coordinate').Coordinate[]} coordinates
 * @returns `true` if coordinates are clockwise
 */
export function clockwise(coordinates) {
  let sum = 0
  for (let i = 0; i < coordinates.length - 1; i++) {
    const c1 = coordinates[i]
    const c2 = coordinates[i + 1]
    sum += (c2[0] - c1[0]) * (c2[1] + c1[1])
  }

  return sum >= 0
}

/**
 * Fetches WKT definition for the specified EPSG code
 * @param {number} epsg the code to fetch
 * @returns {Promise<string>} a promise resolving to the specified projection definition _or an empty string_ if there is an error
 */
export function fetchEpsg(epsg) {
  return fetch(
    `https://spatialreference.org/ref/epsg/${epsg}/ogcwkt/`
  ).then(res => (res.ok ? res.text() : ''))
}

/**
 * @interface ProjectionWithMetadata
 * @extends import('proj4').InterfaceProjection
 * @property {{ name?: string }=} GEOGCS
 * @property {string=} srsCode
 */

/**
 *
 * @param {string|undefined} wkt
 * @returns {ProjectionWithMetadata}
 */
export function wktToProj(wkt) {
  if (wkt) {
    try {
      return proj4.Proj(wkt)
    } catch (e) {
      // EPSG.io seems less than reliable to set a proper HTTP status
      // when the CRS is unknown/weird, so this is how we handle it for now...
    }
  }

  return undefined
}
